import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Typography,
    useTheme,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { KeyboardDatePicker } from '@material-ui/pickers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Container } from '../../components'
import { VENDOR } from '../../helpers'
import { toMMDDYY, toSlashMMDDYYYY } from '../../helpers/date'
import { useAppDispatch, useUser } from '../../hooks'
import { useCompany } from '../../hooks/useCompany'
import { getApartmentList, getUserList } from '../../store'
import { SubContractSummaryViewModel } from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'
import {
    INVOICE_CODE_STEP,
    useVendorInvoice,
} from '../../contexts/useVendorInvoice'
import { VendorInvoiceCodeGate } from '../Invoice/VendorInvoiceCodeGate'
import {
    fetchSubContractSummary,
    FetchSubContractSummaryParams,
    downloadSubContractSummaryExport,
} from './subContractSummaryApi'
import { Routes } from '../../navigation/config/routes'
import { SubsPricingTab } from './SubsPricing/SubsPricingTab'
import { SubsRulesTab } from './SubsRulesTab'
import { User } from '../../models'
import { filterOptions, WorkorderStatusFilters } from '../VendorScheduler/types'

type QuickFilterKey = 'all' | 'last7' | 'last14'

const DEFAULT_STATUS_FILTER: WorkorderStatusFilters = 'ALL'
const ALL_PROPERTIES = -1

const emptySummary: SubContractSummaryViewModel = {
    grand_total_payout: 0,
    workorder_count: 0,
    subs: [],
}

const startOfDay = (d: Date): Date => {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x
}

const endOfDay = (d: Date): Date => {
    const x = new Date(d)
    x.setHours(23, 59, 59, 999)
    return x
}

/** Calendar end = today, start = today minus `daysBack` (e.g. 7 → 5/12 when today is 5/19). */
const getPresetCalendarRange = (daysBack: number) => {
    const today = new Date()
    const endDate = startOfDay(today)
    const startDate = startOfDay(today)
    startDate.setDate(startDate.getDate() - daysBack)
    return { startDate, endDate }
}

const getDefaultDateRange = () => getPresetCalendarRange(7)

const sameCalendarDay = (a: Date, b: Date) =>
    startOfDay(a).getTime() === startOfDay(b).getTime()

/**
 * Highlights a quick filter only when the pickers exactly match that shortcut (or both empty for All).
 */
const getActiveQuickFilter = (
    start: Date | null,
    end: Date | null,
): QuickFilterKey | null => {
    if (start == null && end == null) {
        return 'all'
    }
    if (start == null || end == null) {
        return null
    }
    const { startDate: s7, endDate: e7 } = getPresetCalendarRange(7)
    if (sameCalendarDay(start, s7) && sameCalendarDay(end, e7)) {
        return 'last7'
    }
    const { startDate: s14, endDate: e14 } = getPresetCalendarRange(14)
    if (sameCalendarDay(start, s14) && sameCalendarDay(end, e14)) {
        return 'last14'
    }
    return null
}

const formatUsd = (n: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(n)

export const ViewSubsPage = () => {
    const theme = useTheme()
    const history = useHistory()
    const location = useLocation()
    const params = useParams<{ subUserId?: string }>()
    const dispatch = useAppDispatch()
    const { workspaceUser } = useUser()
    const { apartmentList } = useCompany()
    const vendorInvoiceController = useVendorInvoice()

    const [rangeStart, setRangeStart] = useState<Date | null>(() => {
        const { startDate } = getDefaultDateRange()
        return startDate
    })
    const [rangeEnd, setRangeEnd] = useState<Date | null>(() => {
        const { endDate } = getDefaultDateRange()
        return endDate
    })
    const [propertyFilterId, setPropertyFilterId] = useState(ALL_PROPERTIES)
    const [statusFilter, setStatusFilter] = useState<WorkorderStatusFilters>(
        DEFAULT_STATUS_FILTER,
    )
    const [summary, setSummary] = useState<SubContractSummaryViewModel>(
        emptySummary,
    )
    const [loading, setLoading] = useState(true)
    const [csvDownloading, setCsvDownloading] = useState(false)
    const [selectedTab, setSelectedTab] = useState(() =>
        location.pathname.startsWith(Routes.vendor.subsPricingRoot) ? 1 : 0,
    )
    const [ruleTeamUsers, setRuleTeamUsers] = useState<User[]>([])
    const [ruleTeamUsersLoading, setRuleTeamUsersLoading] = useState(false)

    const isVendor = workspaceUser?.active_workspace?.company_type === VENDOR

    useEffect(() => {
        if (isVendor) {
            void dispatch(getApartmentList({ params: {} }))
        }
    }, [dispatch, isVendor])

    const propertyOptions = useMemo(
        () =>
            [...apartmentList].sort((a, b) =>
                (a.name ?? '').localeCompare(b.name ?? '', undefined, {
                    sensitivity: 'base',
                }),
            ),
        [apartmentList],
    )

    useEffect(() => {
        if (location.pathname.startsWith(Routes.vendor.subsPricingRoot)) {
            setSelectedTab(1)
        }
    }, [location.pathname])

    useEffect(() => {
        if (selectedTab !== 2 || !isVendor) {
            return
        }
        let cancelled = false
        setRuleTeamUsersLoading(true)
        void dispatch(getUserList({ params: { my_team: true } }))
            .then((res) => {
                if (cancelled) return
                const data = (res as { data?: User[] })?.data
                setRuleTeamUsers(Array.isArray(data) ? data : [])
            })
            .catch(() => {
                if (!cancelled) {
                    setRuleTeamUsers([])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setRuleTeamUsersLoading(false)
                }
            })
        return () => {
            cancelled = true
        }
    }, [selectedTab, isVendor, dispatch])

    const handleSubsTabsChange = (_: unknown, value: number) => {
        setSelectedTab(value)
        if (value === 1) {
            if (!location.pathname.startsWith(Routes.vendor.subsPricingRoot)) {
                history.push(Routes.vendor.subsPricingRoot)
            }
        } else if (
            location.pathname.startsWith(Routes.vendor.subsPricingRoot)
        ) {
            history.push(Routes.vendor.subs)
        }
    }

    const needsInvoiceCodeGate =
        isVendor && vendorInvoiceController.step === INVOICE_CODE_STEP

    const activeQuickFilter = useMemo(
        () => getActiveQuickFilter(rangeStart, rangeEnd),
        [rangeStart, rangeEnd],
    )

    const queryParams = useMemo((): FetchSubContractSummaryParams => {
        const base: FetchSubContractSummaryParams = {}
        if (isVendor) {
            base.all_jobsites = true
        }

        if (rangeStart && rangeEnd) {
            const start = startOfDay(rangeStart)
            const end = endOfDay(rangeEnd)
            if (start > end) {
                return base
            }
            Object.assign(base, {
                start_date: toSlashMMDDYYYY(start),
                end_date: toSlashMMDDYYYY(end),
            })
        }

        if (propertyFilterId !== ALL_PROPERTIES) {
            base.apartment = propertyFilterId
        }

        if (statusFilter !== DEFAULT_STATUS_FILTER) {
            base.wo_status = statusFilter
        }

        return base
    }, [isVendor, rangeStart, rangeEnd, propertyFilterId, statusFilter])

    const loadSummary = useCallback(async () => {
        setLoading(true)
        try {
            const data = await fetchSubContractSummary(queryParams)
            setSummary(data)
        } catch (e: unknown) {
            const err = e as {
                response?: { data?: { message?: string } }
                message?: string
            }
            const message =
                err?.response?.data?.message ??
                err?.message ??
                'Could not load subcontractor summary.'
            toast.error(message)
            setSummary(emptySummary)
        } finally {
            setLoading(false)
        }
    }, [queryParams])

    useEffect(() => {
        if (needsInvoiceCodeGate) {
            return
        }
        loadSummary()
    }, [loadSummary, needsInvoiceCodeGate])

    const handleDownloadCsv = useCallback(async () => {
        setCsvDownloading(true)
        try {
            await downloadSubContractSummaryExport(queryParams)
            toast.success('Excel download started.')
        } catch (e: unknown) {
            const err = e as {
                response?: { data?: { message?: string } }
                message?: string
            }
            const message =
                err?.response?.data?.message ??
                err?.message ??
                'Could not download Excel.'
            toast.error(message)
        } finally {
            setCsvDownloading(false)
        }
    }, [queryParams])

    const quickFilterButtonSx = (key: QuickFilterKey) => {
        const selected = activeQuickFilter === key
        return {
            textTransform: 'none' as const,
            borderRadius: 8,
            minWidth: 72,
            padding: theme.spacing(0.75, 2),
            boxShadow: 'none',
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: selected
                ? theme.palette.primary.main
                : theme.palette.grey[300],
            backgroundColor: selected
                ? theme.palette.primary.light
                : theme.palette.common.white,
            color: selected
                ? theme.palette.primary.dark
                : theme.palette.text.primary,
        }
    }

    const clearAllFilters = useCallback(() => {
        const { startDate, endDate } = getDefaultDateRange()
        setRangeStart(startDate)
        setRangeEnd(endDate)
        setPropertyFilterId(ALL_PROPERTIES)
        setStatusFilter(DEFAULT_STATUS_FILTER)
    }, [])

    const hasNonDefaultFilters =
        propertyFilterId !== ALL_PROPERTIES ||
        statusFilter !== DEFAULT_STATUS_FILTER ||
        activeQuickFilter !== 'last7'

    const woCount = summary.workorder_count
    const subCount = summary.subs.length
    const summarySubline = `${woCount} workorder${
        woCount === 1 ? '' : 's'
    } · ${subCount} sub${subCount === 1 ? '' : 's'}`

    if (!workspaceUser) {
        return (
            <Container flex={1} style={{ justifyContent: 'center' }}>
                <CircularProgress
                    style={{ alignSelf: 'center', justifySelf: 'center' }}
                    size={100}
                />
            </Container>
        )
    }

    if (needsInvoiceCodeGate) {
        return <VendorInvoiceCodeGate layout="fullPage" />
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                alignItems: 'stretch',
                alignSelf: 'flex-start',
                maxWidth: selectedTab === 1 ? 1320 : 960,
                margin: 0,
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            <Tabs
                value={selectedTab}
                onChange={handleSubsTabsChange}
                style={{ width: '100%', marginBottom: theme.spacing(2) }}
            >
                <Tab label="Payouts" />
                <Tab label="Pricing" />
                <Tab label="Rules" />
            </Tabs>

            {selectedTab === 0 && (
                <>
                    <Typography
                        color="textSecondary"
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        Date range
                    </Typography>

                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Container
                            direction="row"
                            style={{
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                marginBottom: theme.spacing(1.5),
                                gap: theme.spacing(1),
                            }}
                        >
                            <Button
                                disableElevation
                                variant="text"
                                onClick={() => {
                                    setRangeStart(null)
                                    setRangeEnd(null)
                                }}
                                style={quickFilterButtonSx('all')}
                            >
                                All
                            </Button>
                            <Button
                                disableElevation
                                variant="text"
                                onClick={() => {
                                    const {
                                        startDate,
                                        endDate,
                                    } = getPresetCalendarRange(7)
                                    setRangeStart(startDate)
                                    setRangeEnd(endDate)
                                }}
                                style={quickFilterButtonSx('last7')}
                            >
                                Last 7
                            </Button>
                            <Button
                                disableElevation
                                variant="text"
                                onClick={() => {
                                    const {
                                        startDate,
                                        endDate,
                                    } = getPresetCalendarRange(14)
                                    setRangeStart(startDate)
                                    setRangeEnd(endDate)
                                }}
                                style={quickFilterButtonSx('last14')}
                            >
                                Last 14
                            </Button>
                        </Container>

                        <Container style={{ alignItems: 'flex-start' }}>
                            <KeyboardDatePicker
                                label="Start"
                                value={rangeStart}
                                onChange={(d) => setRangeStart(d)}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                size="small"
                                style={{
                                    width: 200,
                                    marginRight: theme.spacing(2),
                                }}
                            />
                            <KeyboardDatePicker
                                label="End"
                                value={rangeEnd}
                                onChange={(d) => setRangeEnd(d)}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                size="small"
                                style={{ width: 200 }}
                            />
                        </Container>
                    </Container>

                    <Typography
                        color="textSecondary"
                        style={{
                            ...typographyStyle(theme.typography.caption),
                            marginBottom: theme.spacing(0.5),
                        }}
                    >
                        Filters
                    </Typography>

                    <Container
                        style={{
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            marginBottom: theme.spacing(2),
                            gap: theme.spacing(2),
                        }}
                    >
                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{ minWidth: 200 }}
                        >
                            <InputLabel id="view-subs-property-filter-label">
                                Property
                            </InputLabel>
                            <Select
                                labelId="view-subs-property-filter-label"
                                value={propertyFilterId}
                                onChange={(event) => {
                                    setPropertyFilterId(
                                        Number(event.target.value),
                                    )
                                }}
                                label="Property"
                            >
                                <MenuItem value={ALL_PROPERTIES}>All</MenuItem>
                                {propertyOptions.map((property) => (
                                    <MenuItem
                                        key={property.id}
                                        value={property.id}
                                    >
                                        {property.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{ minWidth: 200 }}
                        >
                            <InputLabel id="view-subs-status-filter-label">
                                Status
                            </InputLabel>
                            <Select
                                labelId="view-subs-status-filter-label"
                                value={statusFilter}
                                onChange={(event) => {
                                    setStatusFilter(
                                        event.target
                                            .value as WorkorderStatusFilters,
                                    )
                                }}
                                label="Status"
                            >
                                {filterOptions.map((option) => (
                                    <MenuItem
                                        key={`VIEW_SUBS_STATUS_${option.be}`}
                                        value={option.be}
                                    >
                                        {option.d}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {hasNonDefaultFilters && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={clearAllFilters}
                                style={{ textTransform: 'none' }}
                            >
                                Clear filters
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            disabled={csvDownloading || loading}
                            onClick={() => {
                                void handleDownloadCsv()
                            }}
                            style={{ textTransform: 'none' }}
                        >
                            {csvDownloading ? (
                                <CircularProgress size={22} color="inherit" />
                            ) : (
                                'Download Excel'
                            )}
                        </Button>
                    </Container>

                    {loading ? (
                        <Container
                            justifyContent="center"
                            style={{ padding: theme.spacing(4) }}
                        >
                            <CircularProgress />
                        </Container>
                    ) : (
                        <>
                            <Paper
                                variant="outlined"
                                style={{
                                    padding: theme.spacing(2),
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                <Typography
                                    color="textSecondary"
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                    }}
                                >
                                    Total owed
                                </Typography>
                                <Typography
                                    style={{
                                        ...typographyStyle(theme.typography.h4),
                                        fontWeight: 800,
                                        marginTop: theme.spacing(0.5),
                                    }}
                                >
                                    {formatUsd(summary.grand_total_payout)}
                                </Typography>
                                <Typography
                                    color="textSecondary"
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        marginTop: theme.spacing(0.5),
                                    }}
                                >
                                    {summarySubline}
                                </Typography>
                            </Paper>

                            {summary.subs.length === 0 ? (
                                <Typography color="textSecondary">
                                    No subcontractor assignments match these
                                    filter criteria.
                                </Typography>
                            ) : (
                                summary.subs.map((sub) => (
                                    <Accordion
                                        key={sub.user_id}
                                        style={{
                                            marginBottom: theme.spacing(1),
                                        }}
                                        defaultExpanded={false}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                        >
                                            <Container
                                                style={{
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    paddingRight: theme.spacing(
                                                        2,
                                                    ),
                                                }}
                                            >
                                                <Typography
                                                    style={{
                                                        ...typographyStyle(
                                                            theme.typography
                                                                .subtitle1,
                                                        ),
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {sub.display_name} (
                                                    {sub.workorder_count})
                                                </Typography>
                                                <Typography
                                                    style={{
                                                        ...typographyStyle(
                                                            theme.typography
                                                                .subtitle1,
                                                        ),
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {formatUsd(
                                                        sub.payout_total,
                                                    )}
                                                </Typography>
                                            </Container>
                                        </AccordionSummary>
                                        <AccordionDetails
                                            style={{
                                                display: 'block',
                                                padding: theme.spacing(
                                                    0,
                                                    2,
                                                    2,
                                                    2,
                                                ),
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '100%',
                                                    overflowX: 'auto',
                                                }}
                                            >
                                                <Table
                                                    size="small"
                                                    style={{
                                                        width: 'max-content',
                                                        minWidth: '100%',
                                                        tableLayout: 'auto',
                                                    }}
                                                >
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                WO #
                                                            </TableCell>
                                                            <TableCell>
                                                                Property
                                                            </TableCell>
                                                            <TableCell>
                                                                Service
                                                            </TableCell>
                                                            <TableCell>
                                                                Unit
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Areas
                                                            </TableCell>
                                                            <TableCell>
                                                                Date
                                                            </TableCell>
                                                            <TableCell>
                                                                Status
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Cost
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {sub.workorders.map(
                                                            (row) => (
                                                                <TableRow
                                                                    key={row.id}
                                                                >
                                                                    <TableCell>
                                                                        {row.id}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.property_name ||
                                                                            '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.service_name ||
                                                                            '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.unit_name ||
                                                                            '—'}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {
                                                                            row.service_area_count
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {row.start_date
                                                                            ? toMMDDYY(
                                                                                  new Date(
                                                                                      row.start_date,
                                                                                  ),
                                                                              )
                                                                            : '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            row.status
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {formatUsd(
                                                                            row.payout,
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ),
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            )}
                        </>
                    )}
                </>
            )}

            {selectedTab === 1 && (
                <SubsPricingTab routeSubUserId={params.subUserId} />
            )}

            {selectedTab === 2 && (
                <SubsRulesTab
                    apartmentList={propertyOptions.map((a) => ({
                        id: a.id,
                        name: a.name ?? '',
                    }))}
                    userList={ruleTeamUsers}
                    userListLoading={ruleTeamUsersLoading}
                />
            )}
        </Container>
    )
}
